import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Coin, ArrowUpRight, ArrowDownRight, ShoppingCart, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const planLabels = {
  pro_monthly: 'Premium 1 mese',
  pro_semiannual: 'Premium 6 mesi',
  pro_annual: 'Premium annuale',
  pro_annual_centro: 'Premium annuale Centro',
};

const reasonLabels = {
  signup_bonus: 'Bonus di benvenuto',
  prestazione_completata: 'Prestazione conclusa',
  riserva_prestazione: 'Crediti riservati (prestazione accettata)',
  riserva_rilasciata: 'Riserva liberata',
  prestazione_annullata: 'Riserva liberata (prestazione annullata)',
  richiesta_annullata: 'Riserva liberata (richiesta annullata)',
  booking_lead: 'Lead prenotazione',
  crediti_abbonamento: 'Crediti abbonamento Premium',
  stripe_topup: 'Ricarica crediti (Stripe)',
  topup: 'Ricarica crediti',
  wallet_topup: 'Ricarica crediti',
};

const WalletPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'professional') {
      toast.error('Il wallet è riservato ai professionisti');
      navigate('/');
      return;
    }
    fetchAll();
  }, [user, authLoading, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [w, t, c] = await Promise.all([
        api.get('/api/professionals/me/wallet'),
        api.get('/api/professionals/me/wallet/transactions?limit=50'),
        api.get('/api/platform-config'),
      ]);
      setWallet(w.data);
      setTransactions(t.data);
      setPackages(c.data.credit_recharge_packages || []);
    } catch (e) {
      toast.error('Errore caricamento wallet');
    }
    setLoading(false);
  };

  const purchase = async (pkg) => {
    setPurchasing(pkg.code);
    try {
      const r = await api.post('/api/wallet/purchase', {
        package_code: pkg.code,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio pagamento');
      setPurchasing(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 py-12">Caricamento wallet…</div>
      </div>
    );
  }

  const balance = wallet?.balance ?? 0;
  const reserved = wallet?.reserved ?? 0;
  const available = balance - reserved;
  const lost7 = wallet?.lost_leads_7d ?? 0;
  const incl = wallet?.included_services;
  const inclPct = incl ? Math.min(100, Math.round((incl.used / incl.included) * 100)) : 0;
  const balanceColor = available <= 0 ? 'text-red-600' : available < 20 ? 'text-amber-600' : 'text-sky-600';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="wallet-page">
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl text-foreground mb-2">Il tuo Wallet</h1>
          <p className="text-foreground/70">
            Ricevi 50 crediti di benvenuto alla registrazione. I crediti vengono scalati solo quando una
            prestazione viene <strong>conclusa</strong>.
          </p>
        </div>

        {wallet?.completion_cost != null && (
          <Card className="neumorphism rounded-2xl p-6 mb-6" data-testid="wallet-cost-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-xs text-foreground/60 uppercase tracking-wider mb-1">Costo per prestazione conclusa</div>
                <div className="text-3xl font-heading font-semibold text-sky-700" data-testid="wallet-completion-cost">
                  {wallet.completion_cost} crediti
                </div>
                <div className="text-sm text-foreground/60 mt-1">
                  Piano attuale: {wallet.is_premium ? (planLabels[wallet.premium_plan] || 'Premium') : 'Senza Premium'}
                </div>
              </div>
              <div className="text-sm text-foreground/70 space-y-1">
                <div>Senza Premium: <strong>{wallet.completion_costs?.default} crediti</strong></div>
                <div>Premium 1 mese: <strong>{wallet.completion_costs?.pro_monthly} crediti</strong></div>
                <div>Premium 6 mesi: <strong>{wallet.completion_costs?.pro_semiannual} crediti</strong></div>
                <div>Premium annuale: <strong>{wallet.completion_costs?.pro_annual} crediti</strong></div>
              </div>
            </div>
            {!wallet.is_premium && (
              <Button
                variant="outline"
                className="mt-4 rounded-full"
                onClick={() => navigate('/premium')}
                data-testid="wallet-upgrade-btn"
              >
                Passa a Premium e paga meno per prestazione
              </Button>
            )}
          </Card>
        )}

        {incl && (
          <Card className="neumorphism rounded-2xl p-6 mb-6" data-testid="wallet-included-card">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
              <div>
                <div className="text-xs text-foreground/60 uppercase tracking-wider mb-1">
                  Prestazioni incluse nel mese di abbonamento
                </div>
                <div className="text-3xl font-heading font-semibold text-sky-700" data-testid="wallet-included-counter">
                  {incl.used} di {incl.included} usate
                </div>
                <div className="text-sm text-foreground/60 mt-1">
                  Te ne restano <strong>{incl.left}</strong> fino al{' '}
                  {new Date(incl.period_end).toLocaleDateString('it-IT')}
                </div>
              </div>
              <div className="text-sm text-foreground/70 text-right">
                <div>{incl.label || planLabels[incl.plan]}</div>
                <div>{incl.credits_per_service} crediti per prestazione</div>
                {incl.rollover && <div className="text-xs text-foreground/50 mt-1">Crediti residui cumulabili</div>}
              </div>
            </div>
            <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${inclPct >= 100 ? 'bg-amber-500' : 'bg-sky-500'}`}
                style={{ width: `${inclPct}%` }}
                data-testid="wallet-included-progress"
              />
            </div>
            {incl.left === 0 && (
              <div className="mt-3 text-sm text-amber-800" data-testid="wallet-included-exhausted">
                Hai usato tutte le prestazioni incluse di questo mese: puoi continuare a lavorare con i
                crediti residui o ricaricare il wallet.
              </div>
            )}
          </Card>
        )}

        {/* Balance card */}
        <Card className="neumorphism rounded-2xl p-8 mb-8" data-testid="wallet-balance-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-foreground/60 text-sm uppercase tracking-wider mb-1">
                <Coin size={18} weight="duotone" /> Crediti disponibili
              </div>
              <div className={`text-6xl font-heading font-semibold ${balanceColor}`} data-testid="wallet-balance">
                {available}
              </div>
              <div className="text-foreground/60 text-sm mt-1">
                saldo {balance}
                {reserved > 0 && <> · <span className="text-amber-700" data-testid="wallet-reserved">{reserved} riservati per prestazioni accettate</span></>}
              </div>
              {lost7 > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="wallet-lost-leads">
                  <strong>{lost7}</strong> {lost7 === 1 ? 'richiesta persa' : 'richieste perse'} negli ultimi 7 giorni per credito insufficiente
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-foreground/60 uppercase">Totale accreditato</div>
              <div className="text-xl font-semibold">{wallet?.total_credited ?? 0}</div>
              <div className="text-xs text-foreground/60 uppercase mt-2">Totale utilizzato</div>
              <div className="text-xl font-semibold">{wallet?.total_debited ?? 0}</div>
            </div>
          </div>
          {available <= 0 && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="wallet-empty-alert">
              Non hai crediti disponibili: senza credito non riceverai nuove richieste e non potrai
              accettare prestazioni. Acquista un pacchetto qui sotto.
            </div>
          )}
        </Card>

        {/* Packages */}
        <div className="mb-8">
          <h2 className="font-heading font-semibold text-2xl mb-4 flex items-center gap-2">
            <ShoppingCart size={22} weight="duotone" /> Ricarica crediti
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.map((pkg, idx) => {
              const isBest = idx === packages.length - 1;
              return (
                <Card
                  key={pkg.code}
                  className={`neumorphism rounded-2xl p-6 relative ${isBest ? 'ring-2 ring-primary' : ''}`}
                  data-testid={`pkg-card-${pkg.code}`}
                >
                  {isBest && (
                    <Badge className="absolute -top-2 right-4 bg-primary text-primary-foreground">
                      <Sparkle size={12} className="mr-1" /> Miglior valore
                    </Badge>
                  )}
                  <div className="text-4xl font-heading font-semibold mb-1">€{pkg.eur}</div>
                  <div className="text-foreground/60 text-sm mb-4">{pkg.credits} crediti</div>
                  <div className="text-sm text-foreground/70 mb-6">{pkg.label}</div>
                  <Button
                    className="w-full rounded-full"
                    onClick={() => purchase(pkg)}
                    disabled={purchasing === pkg.code}
                    data-testid={`buy-${pkg.code}-btn`}
                  >
                    {purchasing === pkg.code ? 'Reindirizzamento…' : 'Acquista'}
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Transactions */}
        <div>
          <h2 className="font-heading font-semibold text-2xl mb-4">Movimenti</h2>
          <Card className="neumorphism rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="p-3">Data</th>
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Motivo</th>
                  <th className="p-3 text-right">Importo</th>
                  <th className="p-3 text-right">Saldo dopo</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr><td colSpan="5" className="p-6 text-center text-foreground/60">Nessun movimento</td></tr>
                )}
                {transactions.map((tx) => (
                  <tr key={tx.tx_id} className="border-t border-border/50" data-testid={`tx-row-${tx.tx_id}`}>
                    <td className="p-3 text-foreground/70">{new Date(tx.created_at).toLocaleString('it-IT')}</td>
                    <td className="p-3">
                      {tx.kind === 'credit' ? (
                        <span className="inline-flex items-center gap-1 text-sky-700">
                          <ArrowUpRight size={14} /> Accredito
                        </span>
                      ) : tx.kind === 'hold' ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Coin size={14} /> Riserva
                        </span>
                      ) : tx.kind === 'release' ? (
                        <span className="inline-flex items-center gap-1 text-foreground/60">
                          <ArrowUpRight size={14} /> Riserva liberata
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700">
                          <ArrowDownRight size={14} /> Addebito
                        </span>
                      )}
                    </td>
                    <td className="p-3">{reasonLabels[tx.reason] || tx.reason}</td>
                    <td className={`p-3 text-right font-semibold ${tx.kind === 'credit' ? 'text-sky-700' : tx.kind === 'debit' ? 'text-red-700' : 'text-amber-700'}`}>
                      {tx.kind === 'credit' ? '+' : tx.kind === 'debit' ? '-' : ''}{tx.amount}
                    </td>
                    <td className="p-3 text-right">{tx.balance_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WalletPage;
